import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Card,
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
  Switch,
  SwitchLabel,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { PENDING_REQUEST_NOTIFICATION } from '../../constants/singleFlow.js';
import { type FlowFormInput } from '../../types/singleFlow.js';

const PendingRequestNotification = () => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FlowFormInput>();

  const isNotificationEnabled = useWatch({
    control,
    name: 'pendingRequestNotification.enabled',
  });

  const frequencyDaysErrorMessage =
    errors.pendingRequestNotification?.frequencyDays?.message;
  const frequencyDaysFieldDisabled = !isNotificationEnabled || isReadOnly;

  return (
    <Card className="w-full" hover="noHover">
      <div className="grid gap-4">
        <div className="flex gap-3">
          <h6 className={cn(typographyVariants({ variant: 'h6' }), 'grow')}>
            {t('Approval reminders')}
          </h6>
          <Controller
            control={control}
            name="pendingRequestNotification.enabled"
            render={({ field }) => {
              return (
                <SwitchLabel
                  htmlFor="pending-request-notification-check"
                  className={typographyVariants({
                    variant: 'bodyShort01',
                    color: 'secondary',
                  })}
                >
                  <Switch
                    data-dd-action-name="Approval reminders: toggle"
                    id="pending-request-notification-check"
                    size="small"
                    checked={field.value}
                    onClick={() => field.onChange(!field.value)}
                    disabled={isReadOnly}
                  />
                  {field.value ? t('Enable') : t('Disable')}
                </SwitchLabel>
              );
            }}
          />
        </div>
        <p
          className={typographyVariants({
            variant: 'bodyLong01',
            color: 'secondary',
          })}
        >
          {t(
            'Set up automatic reminders to prompt approvers about pending approval requests.',
          )}
        </p>
        <InputRoot>
          <InputLabel
            htmlFor="pending-request-notification-frequency"
            label={t('Repeat every (days)')}
            disabled={frequencyDaysFieldDisabled}
          />
          <Input
            {...register('pendingRequestNotification.frequencyDays', {
              valueAsNumber: true,
            })}
            data-dd-action-name="Notification frequency input field"
            className="w-36"
            id="pending-request-notification-frequency"
            type="number"
            color={frequencyDaysErrorMessage ? 'error' : 'default'}
            disabled={frequencyDaysFieldDisabled}
            min={PENDING_REQUEST_NOTIFICATION.MIN_FREQUENCY_DAYS}
            max={PENDING_REQUEST_NOTIFICATION.MAX_FREQUENCY_DAYS}
          />
          {frequencyDaysErrorMessage ? (
            <InputHelperText
              color="error"
              helperText={frequencyDaysErrorMessage}
            />
          ) : null}
        </InputRoot>
      </div>
    </Card>
  );
};

export default PendingRequestNotification;
